import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * The CSV → XLSX converter utility (Item Master → Utilities), rebuilt on the
 * staged-upload rails.
 *
 * The browser PUTs the delimited file straight to blob storage against a
 * presigned URL from `getUploadUrl`, then `convert` claims the staged blob:
 * it persists the source as a document, registers a `core_background_jobs`
 * row, and hands the conversion to a Temporal workflow that streams the
 * workbook back into storage as a second document. No user-facing request
 * ever carries file bytes — which is what retired the duplex streaming
 * endpoint this replaces (GEN-2322: response backpressure could freeze the
 * upload mid-flight), and it leaves both files stored rather than evaporating
 * into a one-shot download.
 */

/**
 * The presign-time ceiling, matching the delimited-text pricelist cap. The
 * converter itself is size-indifferent (it streams), but an unbounded
 * presigned PUT invites parking arbitrary blobs in the container.
 */
export const CSV_CONVERSION_MAX_UPLOAD_BYTES = 1024 * 1024 * 1024; // 1 GB

export const GetCsvConversionUploadUrlInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  /** The browser-reported MIME type; used only as the PUT's Content-Type. */
  contentType: z.string().min(1),
  fileSizeBytes: z.number().int().positive(),
});

export const GetCsvConversionUploadUrlResponseSchema = z.object({
  /** Opaque staging key, handed back verbatim to `convert`. */
  uploadKey: z.string(),
  uploadUrl: z.string().url(),
});

/**
 * The parse dialect the user chose, or absent to let the server detect the
 * delimiter from the file's first bytes.
 *
 * Lengths are permissive (a single code point can be two UTF-16 units); the
 * handler enforces the real rules — exactly one usable code point, none of the
 * characters that would collide with record ends or quoting — so a bad value
 * fails with a message rather than a schema error.
 */
export const CsvConversionDialectSchema = z.object({
  delimiter: z.string().min(1).max(2),
  /** Empty string means the file has no quoting; absent means the default `"`. */
  quote: z.string().max(2).optional(),
});

export const ConvertCsvInputSchema = z.object({
  /** The staging key `getUploadUrl` answered with, its blob now uploaded. */
  uploadKey: z.string().min(1),
  fileName: z.string().min(1).max(255),
  dialect: CsvConversionDialectSchema.optional(),
});

export const ConvertCsvResponseSchema = z.object({
  /** The stored delimited source file. */
  sourceDocumentId: z.uuid(),
  /** The `core_background_jobs` row tracking the conversion. */
  backgroundJobId: z.uuid(),
});

/** One stored conversion: the workbook, with the source it was made from. */
export const CsvConversionListItemSchema = z.object({
  xlsxDocumentId: z.uuid(),
  xlsxFileName: z.string(),
  xlsxSizeBytes: z.number().nullable(),
  /** Null when the source document has been removed since. */
  sourceDocumentId: z.uuid().nullable(),
  sourceFileName: z.string().nullable(),
  sourceSizeBytes: z.number().nullable(),
  /** Display name of whoever ran the conversion, when still resolvable. */
  uploadedBy: z.string().nullable(),
  createdAt: z.date(),
});

export const ListCsvConversionsInputSchema = z.object({
  /** How many of the newest conversions to return. */
  limit: z.number().int().min(1).max(50).default(20),
});

export const ListCsvConversionsResponseSchema = z.object({
  conversions: z.array(CsvConversionListItemSchema),
});

export const GetCsvConversionDownloadUrlInputSchema = z.object({
  /** Either side of a conversion — the source document or the workbook. */
  documentId: z.uuid(),
});

export const GetCsvConversionDownloadUrlResponseSchema = z.object({
  /** Short-lived signed URL for the document's bytes. */
  url: z.string(),
  fileName: z.string(),
});

export const csvConversionsContracts = {
  getUploadUrl: oc
    .input(GetCsvConversionUploadUrlInputSchema)
    .output(GetCsvConversionUploadUrlResponseSchema)
    .route({
      method: "POST",
      summary: "Reserve a presigned staging URL for a CSV conversion upload",
    }),
  convert: oc
    .input(ConvertCsvInputSchema)
    .output(ConvertCsvResponseSchema)
    .route({
      method: "POST",
      summary:
        "Store an uploaded CSV as a document and start its XLSX conversion",
    }),
  list: oc
    .input(ListCsvConversionsInputSchema)
    .output(ListCsvConversionsResponseSchema)
    .route({
      method: "GET",
      summary: "List stored conversions, newest first",
    }),
  getDownloadUrl: oc
    .input(GetCsvConversionDownloadUrlInputSchema)
    .output(GetCsvConversionDownloadUrlResponseSchema)
    .route({
      method: "GET",
      summary: "Get a short-lived signed URL for a conversion document",
    }),
} as const;

export type GetCsvConversionUploadUrlInput = z.infer<
  typeof GetCsvConversionUploadUrlInputSchema
>;
export type GetCsvConversionUploadUrlResponse = z.infer<
  typeof GetCsvConversionUploadUrlResponseSchema
>;
export type CsvConversionDialect = z.infer<typeof CsvConversionDialectSchema>;
export type ConvertCsvInput = z.infer<typeof ConvertCsvInputSchema>;
export type ConvertCsvResponse = z.infer<typeof ConvertCsvResponseSchema>;
export type CsvConversionListItem = z.infer<typeof CsvConversionListItemSchema>;
export type ListCsvConversionsInput = z.infer<
  typeof ListCsvConversionsInputSchema
>;
export type ListCsvConversionsResponse = z.infer<
  typeof ListCsvConversionsResponseSchema
>;
export type GetCsvConversionDownloadUrlInput = z.infer<
  typeof GetCsvConversionDownloadUrlInputSchema
>;
export type GetCsvConversionDownloadUrlResponse = z.infer<
  typeof GetCsvConversionDownloadUrlResponseSchema
>;
